import { roomSignature } from "./base-room-detection.mjs";

export const BASE_ROOM_TYPES = {
  bedroom: "bedroom",
  barracks: "barracks",
  kitchen: "kitchen",
  storage: "storage",
};

// Room TYPES a detected region can be built into. Region shape/size comes from
// the auto-generated base layout (base-layout.mjs), so the catalog no longer
// carries footprint dimensions — only the name and the campaign-level effects a
// built room contributes. `storage` is the neutral "empty room" placeholder: a
// valid, buildable type that carries no stats (no storage mechanic yet).
export const BASE_ROOM_CATALOG = {
  [BASE_ROOM_TYPES.bedroom]: {
    id: BASE_ROOM_TYPES.bedroom,
    name: "Bedroom",
    housingCapacity: 2,
    buildCost: 2,
  },
  [BASE_ROOM_TYPES.barracks]: {
    id: BASE_ROOM_TYPES.barracks,
    name: "Barracks",
    housingCapacity: 4,
    buildCost: 4,
  },
  [BASE_ROOM_TYPES.kitchen]: {
    id: BASE_ROOM_TYPES.kitchen,
    name: "Kitchen",
    foodConsumptionMultiplier: 0.8,
    buildCost: 4,
  },
  [BASE_ROOM_TYPES.storage]: {
    id: BASE_ROOM_TYPES.storage,
    name: "Storage",
    // Free: it's the neutral "empty room" state, not a real construction.
    buildCost: 0,
  },
};

// Canonical persisted shape: a map of { regionKey: typeId }, where regionKey is
// a detected region's stable signature (roomSignature in base-room-detection).
// Returns a fresh, validated map — unknown types dropped. The legacy array form
// ([ "bedroom", { type, id } ]) from older saves is tolerated and converted.
export function normalizeBaseRooms(rooms) {
  const out = {};
  for (const [key, type] of roomEntries(rooms)) {
    if (typeof key !== "string" || !key) continue;
    if (!BASE_ROOM_CATALOG[type]) continue;
    out[key] = type;
  }
  return out;
}

export function baseRoomStats(base) {
  const rooms = normalizeBaseRooms(base?.rooms);
  const roomCounts = Object.fromEntries(Object.keys(BASE_ROOM_CATALOG).map((type) => [type, 0]));
  let housingCapacity = 0;
  let foodConsumptionMultiplier = 1;

  for (const type of Object.values(rooms)) {
    const definition = BASE_ROOM_CATALOG[type];
    if (!definition) continue;
    roomCounts[type] = (roomCounts[type] ?? 0) + 1;
    housingCapacity += definition.housingCapacity ?? 0;
    if (definition.foodConsumptionMultiplier) {
      foodConsumptionMultiplier = Math.min(foodConsumptionMultiplier, definition.foodConsumptionMultiplier);
    }
  }

  return {
    rooms,
    roomCounts,
    housingCapacity,
    foodConsumptionMultiplier,
  };
}

// Default room-type assignment for a freshly-detected base layout, keyed by
// region signature: the safe room becomes a Barracks (bases are residential),
// one deterministically-chosen non-safe, non-corridor region becomes a Kitchen,
// and everything else stays unbuilt. Deterministic in the safe-room key, so a
// given base is stable across reloads yet varies base to base. `rooms` is
// base-room-detection's `detection.rooms`.
export function seedDefaultRoomTypes(rooms, safeRoomCellKey) {
  const seeded = {};
  if (!Array.isArray(rooms) || !safeRoomCellKey) return seeded;

  if (rooms.some((room) => roomSignature(room) === safeRoomCellKey)) {
    seeded[safeRoomCellKey] = BASE_ROOM_TYPES.barracks;
  }

  const kitchenCandidates = rooms
    .filter((room) => !room.isCorridor && roomSignature(room) !== safeRoomCellKey)
    .sort((a, b) => roomSignature(a).localeCompare(roomSignature(b)));
  if (kitchenCandidates.length) {
    const pick = kitchenCandidates[hashRoomKey(safeRoomCellKey) % kitchenCandidates.length];
    seeded[roomSignature(pick)] = BASE_ROOM_TYPES.kitchen;
  }

  return seeded;
}

// Small stable hash (FNV-1a) so default kitchen placement varies from base to
// base yet stays fixed for a given base.
function hashRoomKey(text) {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) { hash ^= text.charCodeAt(i); hash = Math.imul(hash, 16777619); }
  return hash >>> 0;
}

// Yield [regionKey, typeId] pairs from either the current map form or the
// legacy array form, so old saves and callers keep working through one path.
function roomEntries(rooms) {
  if (!rooms) return [];
  if (Array.isArray(rooms)) {
    return rooms.map((room, index) => {
      const type = typeof room === "string" ? room : room?.type;
      const key = typeof room === "object" && typeof room?.id === "string" && room.id
        ? room.id
        : `room:${type}:${index + 1}`;
      return [key, type];
    });
  }
  if (typeof rooms === "object") return Object.entries(rooms);
  return [];
}
